/* istanbul ignore file */
import { createSlice } from "@reduxjs/toolkit";
import { WEBSOCKET_MSG_TYPE } from "../../util/constants";

const initialState = {
  notificationEvents: {},
  tradeEmpEvents: {},
  tradeAdminEvents: {},
  torEmpEvents: {},
  torAdminEvents: {},
  taedAdminEvents: {},
  bidEmpEvents: {},
  bidAdminEvents: {},
  scheduleAdminEvents: {},
  freezeEvents: {},
};

const MAX_TRANSACTIONS = 50;

const getUniqueId = (wsEvent) => {
  switch (wsEvent.messageType) {
    case WEBSOCKET_MSG_TYPE.TRADE_EVENT:
      return wsEvent.tradeFormId;
    case WEBSOCKET_MSG_TYPE.TOR_EVENT:
      return wsEvent.torId;
    case WEBSOCKET_MSG_TYPE.TAED_EVENT:
    case WEBSOCKET_MSG_TYPE.BID_EVENT:
    case WEBSOCKET_MSG_TYPE.BID_CHOICE_EVENT:
      return wsEvent.referenceNumber;
    case WEBSOCKET_MSG_TYPE.SCHEDULE_EVENT:
      return wsEvent.sk;
    case WEBSOCKET_MSG_TYPE.FREEZE_EVENT:
      return wsEvent.sk;
    case WEBSOCKET_MSG_TYPE.NOTIFICATION:
      return wsEvent.id;
    default:
      console.warn("(getUniqueId) Unknown websocket message type: ", wsEvent.messageType);
  }
};

const manageMaxTransactions = (currentData, newTransaction) => {
  const newTransactionId = getUniqueId(newTransaction);
  if (
    !currentData[newTransactionId] ||
    parseInt(newTransaction.createdAt) > parseInt(currentData[newTransactionId].createdAt)
  ) {
    // Add the new transaction using its ID as the key
    currentData[newTransactionId] = newTransaction;

    // Convert object values into an array, sort by createdAt, and limit to MAX_TRANSACTIONS
    const sortedTransactions = Object.values(currentData)
      .sort((a, b) => parseInt(b.createdAt) - parseInt(a.createdAt))
      .slice(0, MAX_TRANSACTIONS);

    // Rebuild currentData as an object with only the top MAX_TRANSACTIONS transactions
    const updatedData = sortedTransactions.reduce((data, txn) => {
      data[getUniqueId(txn)] = txn;
      return data;
    }, {});

    // console.log("hi")
    // console.log(updatedData)
    return updatedData;
  } else {
    // Old transaction was added, skip
    return currentData;
  }
};

export const webSocketEventSlice = createSlice({
  name: "webSocketEvent",
  initialState,
  reducers: {
    addNotificationEvent: (state, action) => {
      let newState = { ...state.notificationEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.notificationEvents = newState;
    },
    addTradeEmpEvent: (state, action) => {
      let newState = { ...state.tradeEmpEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.tradeEmpEvents = newState;
    },
    addTradeAdminEvent: (state, action) => {
      let newState = { ...state.tradeAdminEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.tradeAdminEvents = newState;
    },
    addTorEmpEvent: (state, action) => {
      let newState = { ...state.torEmpEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.torEmpEvents = newState;
    },
    addTorAdminEvent: (state, action) => {
      let newState = { ...state.torAdminEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.torAdminEvents = newState;
    },
    addTaedAdminEvent: (state, action) => {
      let newState = { ...state.taedAdminEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.taedAdminEvents = newState;
    },
    addBidEmpEvent: (state, action) => {
      let newState = { ...state.bidEmpEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.bidEmpEvents = newState;
    },
    addBidAdminEvent: (state, action) => {
      let newState = { ...state.bidAdminEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.bidAdminEvents = newState;
    },
    addScheduleAdminEvent: (state, action) => {
      let newState = { ...state.scheduleAdminEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.scheduleAdminEvents = newState;
    },
    addFreezeEvent: (state, action) => {
      let newState = { ...state.freezeEvents };
      newState = manageMaxTransactions(newState, action.payload);
      state.freezeEvents = newState;
    },
    removeNotificationEvent: (state, action) => {
      let newState = { ...state.notificationEvents };
      delete newState[action.payload];
      state.notificationEvents = newState;
    },
    removeTradeEmpEvent: (state, action) => {
      let newState = { ...state.tradeEmpEvents };
      delete newState[action.payload];
      state.tradeEmpEvents = newState;
    },
    removeTradeAdminEvent: (state, action) => {
      let newState = { ...state.tradeAdminEvents };
      delete newState[action.payload];
      state.tradeAdminEvents = newState;
    },
    removeTorEmpEvent: (state, action) => {
      let newState = { ...state.torEmpEvents };
      delete newState[action.payload];
      state.torEmpEvents = newState;
    },
    removeTorAdminEvent: (state, action) => {
      let newState = { ...state.torAdminEvents };
      delete newState[action.payload];
      state.torAdminEvents = newState;
    },
    removeTaedAdminEvent: (state, action) => {
      let newState = { ...state.taedAdminEvents };
      delete newState[action.payload];
      state.taedAdminEvents = newState;
    },
    removeBidEmpEvent: (state, action) => {
      let newState = { ...state.bidEmpEvents };
      delete newState[action.payload];
      state.bidEmpEvents = newState;
    },
    removeBidAdminEvent: (state, action) => {
      let newState = { ...state.bidAdminEvents };
      delete newState[action.payload];
      state.bidAdminEvents = newState;
    },
    removeScheduleAdminEvent: (state, action) => {
      let newState = { ...state.scheduleAdminEvents };
      delete newState[action.payload];
      state.scheduleAdminEvents = newState;
    },
    removeFreezeEvent: (state, action) => {
      let newState = { ...state.freezeEvents };
      delete newState[action.payload];
      state.freezeEvents = newState;
    },
    clearStationWebSocketData: (state) => {
      state.tradeEmpEvents = {};
      state.tradeAdminEvents = {};
      state.torEmpEvents = {};
      state.torAdminEvents = {};
      state.taedAdminEvents = {};
      state.bidEmpEvents = {};
      state.bidAdminEvents = {};
      state.scheduleAdminEvents = {};
      state.freezeEvents = {};
    },
    clearAllWebsocketData: () => initialState,
  },
});

export const {
  addNotificationEvent,
  addTradeEmpEvent,
  addTradeAdminEvent,
  addTorEmpEvent,
  addTorAdminEvent,
  addTaedAdminEvent,
  addBidEmpEvent,
  addBidAdminEvent,
  addScheduleAdminEvent,
  addFreezeEvent,
  removeNotificationEvent,
  removeTradeEmpEvent,
  removeTradeAdminEvent,
  removeTorEmpEvent,
  removeTorAdminEvent,
  removeTaedAdminEvent,
  removeBidEmpEvent,
  removeBidAdminEvent,
  removeScheduleAdminEvent,
  removeFreezeEvent,
  clearStationWebSocketData,
  clearAllWebsocketData,
} = webSocketEventSlice.actions;

export const selectWebSocketEventData = (state) => state.webSocketEvent;

export default webSocketEventSlice.reducer;
